// @ts-check
import React, { useCallback, useMemo } from 'react';
import { ButtonVariant, Flex, FlexItem } from '@patternfly/react-core';

import SimpleTable, { SortDirection } from 'components/Common/SimpleTable/SimpleTable';
import { useTranslation } from 'localization/useTranslation';
import T from 'localization/LocalizationKeys';
import _ from 'lodash';
import SelectUsersModal from 'components/Modals/SelectUsersModal';
import useBoolean from 'utils/hooks/useBoolean';
import RelationDropdown from 'components/RelationTable/RelationDropdown';
import { NoRelation, GroupRelationMap } from 'components/RelationTable/RelationRadios';
import { VisibleColumns } from 'components/RelationTable/RelationTable';

/**
 * @typedef {object} ResourceType
 * @prop {string} id
 * @prop {string} displayName
 * @prop {string} type
 * @prop {string} relation
 */

/**
 * @type {(import('components/Common/SimpleTable/SimpleTable').ColumnType<ResourceType> & { columnKey: string })[]}
 */
const defaultTableColumns = [
  {
    columnKey: VisibleColumns.displayName,
    label: T.common.displayname,
    path: 'displayName',
    searchItem: true,
    isSortable: true
  },
  {
    columnKey: VisibleColumns.id,
    label: T.common.id,
    path: 'id',
    isSortable: true
  },
  {
    columnKey: VisibleColumns.type,
    label: T.common.type,
    path: 'type',
    isSortable: true
  }
];

export const createRelation = (groupId, subjectId, relation, type) => {
  return {
    resourceType: 'group',
    resourceId: groupId,
    relation: relation,
    subjectType: type,
    subjectId: subjectId
  };
};

/**
 * @type {import('components/Common/SimpleTable/SimpleTable').SortByType<ResourceType>}
 * @constant
 */
const DefaultSortBy = {
  path: 'displayName',
  direction: SortDirection.DESC
};

/**
 * @typedef GroupRelationTableProps
 * @prop {string} currentUserId - the id of the user
 * @prop {string} groupId - the id of the group
 * @prop {string[]} visibleColumns - the columns to display
 * @prop {Array} resources - the resources to display
 * @prop {boolean} [isLoading=false] - if the table is loading
 * @prop {function} updateRelationsFn - the function to update relations
 * @prop {function} deleteRelationsFn - the function to delete relations
 */

/**
 * @description Table to display relations between an organization and a resource
 * @param {GroupRelationTableProps} props
 * @returns {React.ReactElement} - the table
 */
const GroupRelationTable = ({
  currentUserId,
  groupId,
  visibleColumns = [VisibleColumns.displayName, VisibleColumns.type, VisibleColumns.relation],
  resources, isLoading, updateRelationsFn, deleteRelationsFn }) => {

  const T = useTranslation();
  const [selected, available] = _.partition(resources, resource => resource.relation !== NoRelation);
  const [availableUsers, availableGroups] = _.partition(available, resource => resource.type === 'user');
  const [isShowingUserSelection, , closeUserSelection, toggleUserSelection] = useBoolean(false);
  const [isShowingGroupSelection, , closeGroupSelection, toggleGroupSelection] = useBoolean(false);

  const onRelationChanged = useCallback((relation, oldRelation, row) => {
    const rowId = row.id;
    const newRelationObj = createRelation(groupId, rowId, relation, row.type);
    const oldRelationObj = createRelation(groupId, rowId, oldRelation, row.type);
    if (relation !== NoRelation) {
      updateRelationsFn([newRelationObj]);
    } else {
      deleteRelationsFn(oldRelationObj);
    }
  }, [groupId, updateRelationsFn, deleteRelationsFn]);

  const columns = useMemo(() => {
    /** @type {(import('components/Common/SimpleTable/SimpleTable').ColumnType<ResourceType> & { columnKey: string })[]} */
    const cols = [
      ...defaultTableColumns,
      {
        columnKey: VisibleColumns.relation,
        label: T.common.relation,
        path: 'relation',
        isSortable: false,
        searchItem: true,
        render: (relation, row) => {
          return (
            <>
              <Flex>
                <FlexItem>
                  <RelationDropdown
                    relationsMap={GroupRelationMap}
                    relation={relation}
                    onRelationChanged={(value) => onRelationChanged(value, relation, row)}
                    isDisabled={row.id === currentUserId}
                  />
                </FlexItem>
              </Flex>
            </>
          );
        }
      }
    ];
    return cols.filter((col) => visibleColumns.includes(col.columnKey));
  }, [T.common.relation, currentUserId, onRelationChanged, visibleColumns]);


  const globalActions = useMemo(/** @returns {import('components/Common/SimpleTable/SimpleTable').GlobalAction[]} */() => {
    return [
      {
        label: 'Add Users',
        onClick: () => {
          toggleUserSelection();
        }
      },
      {
        label: 'Add Groups',
        onClick: () => {
          toggleGroupSelection();
        }
      }
    ];
  }, [toggleGroupSelection, toggleUserSelection]);

  const rowActions = useMemo(/** @return {import('components/Common/SimpleTable/SimpleTable').ActionType<ResourceType>[]} */() => {
    return [
      {
        variant: ButtonVariant.danger,
        isOverflow: true,
        title: T.common.delete,
        isDisabled: (row) => {
          return row.id === currentUserId;
        },
        onClick: (row) => {
          const relation = createRelation(groupId, row.id, row.relation, row.type);
          deleteRelationsFn(relation);
        }
      }
    ];
  }, [T.common.delete, currentUserId, deleteRelationsFn, groupId]);

  return (
    <>
      <SimpleTable
        globalActions={globalActions}
        actions={rowActions}
        columns={columns}
        data={selected}
        isLoading={isLoading}
        sortBy={DefaultSortBy}
      />
      <SelectUsersModal
        title={T.common.add}
        tableColumns={defaultTableColumns}
        isOpen={isShowingUserSelection}
        onClose={closeUserSelection}
        available={availableUsers}
        groupId={groupId}
        updateRelationsFn={updateRelationsFn}
      />
      <SelectUsersModal
        title={T.common.add}
        tableColumns={defaultTableColumns}
        isOpen={isShowingGroupSelection}
        onClose={closeGroupSelection}
        available={availableGroups}
        groupId={groupId}
        updateRelationsFn={updateRelationsFn}
      />
    </>
  );
};

export default GroupRelationTable;
